import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useRef,
	useCallback,
	Suspense,
} from 'react';

import {
	usePreloadedQuery,
	useQueryLoader,
	usePaginationFragment,
	type PreloadedQuery,
} from 'react-relay';
import type { Disposable } from 'relay-runtime';

import type { agentSessionsMainPaginationQuery } from './__generated__/agentSessionsMainPaginationQuery.graphql';
import type { agentSessionsMainQuery } from './__generated__/agentSessionsMainQuery.graphql';
import type { agentSessionsMainQuery_query$key } from './__generated__/agentSessionsMainQuery_query.graphql';
import { getAgentSessionReconciliation } from './agentSessionReconciliation';
import {
	agentSessionsMainQuery as query,
	agentSessionsMainFragment,
} from './agentSessionsMainQuery';
import {
	getAgentSessionsMainResource,
	type AgentSessionsMainResource,
} from './agentSessionsMainResource';
import { getAgentSessionsPolicy } from './agentSessionsPolicy';
import { getAgentSessionsScopeKey, useAgentSessionsScope } from './AgentSessionsScopeProvider';
import { useSharedAgentSessionsResource } from './sharedAgentSessionsResource';
import type { AgentSessionsResult } from './types';
import { useAgentSessionsLiveUpdates } from './useAgentSessionsLiveUpdates';
import { useLegacyAgentSessionsData } from './useLegacyAgentSessionsData';

type ResourceContext = {
	resource?: AgentSessionsMainResource;
	legacy?: AgentSessionsResult;
} | null;
export const AgentSessionsDataContext: React.Context<ResourceContext> =
	createContext<ResourceContext>(null);

const LegacyOwner = ({ children }: { children: React.ReactNode }) => {
	const { input } = useAgentSessionsScope();
	const data = useLegacyAgentSessionsData(input);
	useAgentSessionsLiveUpdates({ options: input, data });
	return (
		<AgentSessionsDataContext.Provider value={{ legacy: data.result }}>
			{children}
		</AgentSessionsDataContext.Provider>
	);
};

const PaginationOwner = ({
	resource,
	owner,
	generation,
	queryRef,
	onReady,
}: {
	resource: AgentSessionsMainResource;
	owner: symbol;
	generation: number;
	queryRef: PreloadedQuery<agentSessionsMainQuery>;
	onReady: () => void;
}) => {
	// eslint-disable-next-line @atlassian/relay/query-restriction -- The elected owner and buffered refresh share this resource query.
	const root = usePreloadedQuery<agentSessionsMainQuery>(query, queryRef);
	const { data, hasNext } = usePaginationFragment<
		agentSessionsMainPaginationQuery,
		agentSessionsMainQuery_query$key
	>(agentSessionsMainFragment, root);
	useEffect(() => {
		if (!resource.owns(owner, generation)) return;
		const previous = resource.getSnapshot().value;
		resource.publish(owner, generation, {
			fragment: root,
			pageInfo: {
				hasNextPage: hasNext,
				endCursor: data.agent_sessionQuery?.pageInfo?.endCursor ?? null,
			},
			isLoadingMore: previous?.isLoadingMore ?? false,
			isRefreshing: previous?.isRefreshing ?? false,
			loadMoreError: previous?.loadMoreError ?? null,
			refreshError: previous?.refreshError ?? null,
		});
		resource.setDriver({
			hasNext,
			endCursor: data.agent_sessionQuery?.pageInfo?.endCursor ?? null,
		});
		onReady();
	}, [data, generation, hasNext, onReady, owner, resource, root]);
	return null;
};

class OwnerErrorBoundary extends React.Component<
	{
		onError: (error: Error) => void;
		children: React.ReactNode;
	},
	{ failed: boolean }
> {
	state = { failed: false };
	static getDerivedStateFromError() {
		return { failed: true };
	}
	componentDidCatch(error: Error) {
		this.props.onError(error);
	}
	render() {
		return this.state.failed ? null : this.props.children;
	}
}

const MainOwner = ({
	resource,
	owner,
	generation,
}: {
	resource: AgentSessionsMainResource;
	owner: symbol;
	generation: number;
}) => {
	const [queryRef, loadQuery, disposeQuery] = useQueryLoader<agentSessionsMainQuery>(query);
	const initial = useRef<Disposable | undefined>(undefined);
	const onReady = useCallback(() => {
		initial.current?.dispose();
		initial.current = undefined;
	}, []);
	useEffect(() => {
		initial.current = getAgentSessionReconciliation(resource.environment, resource.scope).begin();
		const events = resource.start();
		loadQuery(resource.variables, { fetchPolicy: resource.initialFetchPolicy });
		return () => {
			disposeQuery();
			onReady();
			events.dispose();
		};
	}, [disposeQuery, loadQuery, onReady, resource]);
	return (
		<OwnerErrorBoundary
			onError={(error) => {
				onReady();
				resource.fail(owner, generation, error);
			}}
		>
			<Suspense fallback={null}>
				{queryRef && (
					<PaginationOwner
						resource={resource}
						owner={owner}
						generation={generation}
						queryRef={queryRef}
						onReady={onReady}
					/>
				)}
			</Suspense>
		</OwnerErrorBoundary>
	);
};

const ScopedProvider = ({
	resource,
	children,
	fallback,
}: {
	resource: AgentSessionsMainResource;
	children: React.ReactNode;
	fallback: React.ReactNode;
}) => {
	const { reader, generation, isOwner, value, error } = useSharedAgentSessionsResource(resource);
	// An ownerless failed snapshot must allow acquisition to clear the previous error.
	if (error && !value && isOwner) throw error;
	return (
		<AgentSessionsDataContext.Provider value={{ resource }}>
			{isOwner && (
				<MainOwner key={generation} resource={resource} owner={reader} generation={generation} />
			)}
			{value ? children : fallback}
		</AgentSessionsDataContext.Provider>
	);
};

export const AgentSessionsDataProvider = ({
	children,
	fallback = null,
}: {
	children: React.ReactNode;
	fallback?: React.ReactNode;
}): React.JSX.Element => {
	const [policy] = useState(getAgentSessionsPolicy);
	const parent = useContext(AgentSessionsDataContext);
	const { environment, scope } = useAgentSessionsScope();
	if (
		parent?.resource?.environment === environment &&
		scope &&
		getAgentSessionsScopeKey(parent.resource.scope) === getAgentSessionsScopeKey(scope)
	)
		return <>{children}</>;
	if (!policy.sharedResources || environment.isServer())
		return <LegacyOwner>{children}</LegacyOwner>;
	if (!scope)
		return (
			<AgentSessionsDataContext.Provider value={{}}>{children}</AgentSessionsDataContext.Provider>
		);
	return (
		<ScopedProvider resource={getAgentSessionsMainResource(environment, scope)} fallback={fallback}>
			{children}
		</ScopedProvider>
	);
};
